import { useCallback, useMemo, useContext } from 'react'
import { connect } from 'react-redux'
import { useIntl } from 'react-intl'
import { addGTMEvent } from 'api/googleTagManager'
import { searchDestinationFormSubmit, searchFormSubmit, trendingDestinationClick } from 'analytics/eventDefinitions'
import { encodeSearchParams } from 'lib/search/searchUtils'
import PopularButtonList from 'components/Search/SearchForm/PopularButtonList'
import GlobalSearchQuickFiltersTitle from '../GlobalSearch/QuickFilters/GlobalSearchQuickFiltersTitle'
import GlobalSearchQuickFilterButtonsContainer from '../GlobalSearch/QuickFilters/GlobalSearchQuickFilterButtonsContainer'
import GlobalSearchQuickFiltersContainer from '../GlobalSearch/QuickFilters/GlobalSearchQuickFiltersContainer'
import { take } from 'lib/array/arrayUtils'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import { getCurrentUserId } from 'selectors/accountSelectors'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { useHotelTrendingDestinations } from 'home/hooks/useHotelTrendingDestinations'
import { DEFAULT_FILTER } from 'home/components/TrendingDestinations/TrendingDestinations'

interface Props {
  onClick?: () => void
  filters?: App.OfferListFilters
  titleWeight?: 'bold' | 'normal'
  titleColour?: 'neutral-eight' | 'neutral-two'
  className?: string
  noTitle?: boolean
  search: string
}

function logEvents() {
  addGTMEvent(searchFormSubmit())
  addGTMEvent(searchDestinationFormSubmit())
  addGTMEvent(trendingDestinationClick())
}

function SearchTrendingDestinations({
  search,
  filters,
  titleColour,
  titleWeight,
  className,
  noTitle = false,
}: Props) {
  const intl = useIntl()
  const { currentRegionCode } = useContext(GeoContext)
  const domainUserId = useAppSelector(state => state.auth.domainUserId)
  const leUserId = useAppSelector(getCurrentUserId)
  const lereDestinationModelEnabled: boolean = !!useOptimizelyExperiment(OptimizelyFeatureFlags.lereDestinationModelEnabled)

  const [reduxTrendingDestinations, fetchingTrendingDestinations] = useHotelTrendingDestinations(
    DEFAULT_FILTER,
    currentRegionCode,
    leUserId,
    domainUserId,
    !(lereDestinationModelEnabled && config.BRAND == 'luxuryescapes'),
  )

  const trendingDestinations = useMemo(() =>
    reduxTrendingDestinations.map(destination => ({
      placeId: destination.destinationId ?? '',
      primaryText: destination.name,
      abbreviation: destination.name,
    })),
  [reduxTrendingDestinations],
  )

  const getQueryParams = useCallback((optionValue: string, optionText: string): string => {
    const searchItem: App.SearchItem = {
      searchType: 'destination',
      value: optionValue,
      format: {
        mainText: optionText,
      },
    }
    const queryParams = encodeSearchParams({ urlSearch: search, searchItem, rooms: [config.search.defaultOccupants], filters, isFlexibleWithDate: true }).toString()

    return queryParams
  }, [filters, search])

  const options = useMemo(() => {
    if (trendingDestinations.length > 0) {
      return trendingDestinations.map(trendingDestination => {
        const queryParams = getQueryParams(trendingDestination.placeId, trendingDestination.primaryText)
        const url = `/search?${queryParams}`

        return {
          value: trendingDestination.placeId,
          text: trendingDestination.primaryText,
          url,
        }
      })
    }
    return []
  }, [trendingDestinations, getQueryParams])

  if (!options.length) {
    return null
  }

  const trendingDestinationsTitle = intl.formatMessage({
    id: '8leONsKkbcd_Mfja9EiE',
    defaultMessage: 'Trending destinations',
    description: 'Title for trending destinations section in search form',
  })

  return (
    <GlobalSearchQuickFiltersContainer className={className}>
      {!noTitle && <GlobalSearchQuickFiltersTitle
        colour={titleColour}
        weight={titleWeight}
      >
        {trendingDestinationsTitle}
      </GlobalSearchQuickFiltersTitle>}
      <GlobalSearchQuickFilterButtonsContainer>
        <PopularButtonList
          onContextMenuHandler={logEvents}
          options={take(options, 11)}
          fetching={fetchingTrendingDestinations}
        />
      </GlobalSearchQuickFilterButtonsContainer>
    </GlobalSearchQuickFiltersContainer>
  )
}

const mapStateToProps = (state: App.State) => ({
  trendingDestinations: state.destination.trendingDestinations,
  fetchingTrendingDestinations: state.destination.fetchingTrendingDestinations,
  search: state.router.location.search,
})

export default connect(mapStateToProps)(SearchTrendingDestinations)
